import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { IdCard, User } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { Button, InlineAlert, TextField } from '@chillout/ui'
import { AdminModal, ModalActions } from '../../../shared/Modal'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { SuccessInfoRow } from '../../../shared/SuccessInfoRow'
import { CopyModalActions } from '../../../shared/CopyModalActions'
import { RoleToggle } from '../RoleToggle'
import { useAddCashier } from './useAddCashier'
import { addCashierSchema } from '../cashierSchemas'
import { useCopyWithFeedback } from '../../useCopyWithFeedback'
import { PinCard } from '../PinCard'

type Step = 'form' | 'pin'

interface AddCashierModalProps {
  isVisible: boolean
  onClose: () => void
}

export function AddCashierModal({ isVisible, onClose }: AddCashierModalProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('form')
  const mutation = useAddCashier()
  const resetMutation = mutation.reset

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: zodResolver(addCashierSchema(t)),
    defaultValues: { name: '', email: '', role: 'staff' as const },
  })

  useEffect(() => {
    if (!isVisible) {
      reset()
      resetMutation()
      setStep('form')
    }
  }, [isVisible, reset, resetMutation])

  const pin = mutation.data?.pin ?? ''
  const { copied, copy: handleCopy } = useCopyWithFeedback(pin)

  const onSubmit = handleSubmit(({ name, email, role }) => {
    mutation.mutate(
      { name: name.trim(), email: email.trim() || null, role },
      { onSuccess: () => setStep('pin') },
    )
  })

  const isPinStep = step === 'pin'
  const watchedName = watch('name')
  const watchedRole = watch('role')

  return (
    <AdminModal
      visible={isVisible}
      onClose={onClose}
      closeAccessibilityLabel={t('operations.addCashier.closeAccessibilityLabel')}
      title={isPinStep ? t('operations.addCashier.pinTitle') : t('operations.addCashier.title')}
      icon={isPinStep ? <SuccessIcon /> : undefined}
      actions={
        isPinStep ? (
          <CopyModalActions
            copied={copied}
            copyLabel={t('operations.addCashier.pinCopyButton')}
            copiedLabel={t('operations.addCashier.pinCopiedButton')}
            doneLabel={t('common.done')}
            onCopy={handleCopy}
            onDone={onClose}
          />
        ) : (
          <ModalActions>
            <Button label={t('common.cancel')} variant="secondary" className="flex-1" onPress={onClose} />
            <Button
              label={t('operations.addCashier.submitButton')}
              loadingLabel={t('operations.addCashier.submittingButton')}
              variant="primary"
              className="flex-1"
              loading={mutation.isPending}
              onPress={onSubmit}
            />
          </ModalActions>
        )
      }
    >
      {isPinStep ? (
        <View className="mt-4 gap-4">
          <SuccessInfoRow
            left={{ icon: <User size={20} color={colors.text.secondary} strokeWidth={2} />, label: watchedName }}
            right={{ icon: <IdCard size={20} color={colors.text.secondary} strokeWidth={2} />, label: t(`operations.roles.${watchedRole}`) }}
          />
          <Text className="text-sm font-normal text-secondary">
            {t('operations.addCashier.pinDescription')}
          </Text>
          <PinCard pin={pin} />
        </View>
      ) : (
        <View className="mt-6 gap-5">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <TextField
                label={t('operations.addCashier.nameLabel')}
                placeholder={t('operations.addCashier.namePlaceholder')}
                value={field.value}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <TextField
                label={t('operations.addCashier.emailLabel')}
                placeholder={t('operations.addCashier.emailPlaceholder')}
                value={field.value}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            )}
          />
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <RoleToggle
                label={t('operations.addCashier.roleLabel')}
                value={field.value}
                staffLabel={t('operations.roles.staff')}
                managerLabel={t('operations.roles.manager')}
                onChange={field.onChange}
              />
            )}
          />
          {mutation.isError && <InlineAlert message={t('operations.addCashier.submitError')} />}
        </View>
      )}
    </AdminModal>
  )
}